/**
 * The rail's fly-out panel, docked beside the rail (board "1 · Command Rail"):
 * a heading naming what it lists, the list, and a Quick access foot with
 * Pinned / Recent.
 *
 *   folder  → that folder's contents at ANY depth (the shared MenuTree, rooted
 *             at the folder's children; a website with pages is rooted at the
 *             site itself so the site stays openable);
 *   all     → the whole menu;
 *   editors → Open editors.
 *
 * Esc anywhere in the panel closes it and the owner returns focus to the rail
 * button that opened it (the shared menus stop Esc themselves when they close
 * a context menu, so that one Esc does not also close the panel).
 */
import { ChevronLeft } from 'lucide-react'
import { forwardRef, useState, type KeyboardEvent } from 'react'
import { tw } from '../../../testHooks'
import { EditorsList } from '../../nav/parts/EditorsList'
import { MenuTree } from '../../nav/parts/MenuTree'
import { PinnedList, RecentList } from '../../nav/parts/ShellLists'
import { S, iconButton } from '../../nav/tokens'
import type { NavTreeNode } from '../../nav/tree'
import type { ShellNavModel } from '../../nav/useShellNav'
import type { RailPanel } from './RailBar'

type Quick = 'pinned' | 'recent'

export interface FlyoutProps {
  id: string
  model: ShellNavModel
  panel: RailPanel
  /** The folder for a `folder` panel (resolved by the owner; undefined → all). */
  folder?: NavTreeNode
  compact: boolean
  emptyNavMessage: string
  onClose: () => void
}

export const Flyout = forwardRef<HTMLElement, FlyoutProps>(function Flyout({
  id, model, panel, folder, compact, emptyNavMessage, onClose,
}, ref) {
  const [quick, setQuick] = useState<Quick>('pinned')
  const kind = panel.kind === 'folder' && !folder ? 'all' : panel.kind
  const n = model.editors.length
  const kicker = kind === 'editors' ? 'Open editors'
    : kind === 'all' ? 'Menu'
      : folder?.isFolder ? 'Folder' : 'Website'
  const title = kind === 'editors' ? `${n} ${n === 1 ? 'record' : 'records'} in progress`
    : kind === 'all' ? 'All screens'
      : folder?.item.title || ''
  const headingId = `${id}-h`

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape' && !e.defaultPrevented) { e.preventDefault(); onClose() }
  }

  const quickTabs: { id: Quick; label: string; count: number }[] = [
    { id: 'pinned', label: 'Pinned', count: model.pinned.length },
    { id: 'recent', label: 'Recent', count: model.recent.length },
  ]
  const onQuickKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return
    e.preventDefault()
    const next: Quick = e.key === 'Home' ? 'pinned' : e.key === 'End' ? 'recent' : quick === 'pinned' ? 'recent' : 'pinned'
    setQuick(next)
    ;(e.currentTarget.querySelector(`[data-tab="${next}"]`) as HTMLElement | null)?.focus()
  }

  const folderRoots = folder ? (folder.isFolder ? folder.children : [folder]) : model.roots

  return (
    <aside ref={ref} id={id} aria-labelledby={headingId} onKeyDown={onKeyDown}
      {...tw('action', 'shell-rail-flyout', { panel: kind, folder: folder?.item.id })}
      style={{
        width: compact ? 260 : 288, flexShrink: 0, minHeight: 0, display: 'flex', flexDirection: 'column',
        background: S.panel, borderRight: `1px solid ${S.border}`, color: S.text,
      }}>
      <div style={{
        flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        padding: compact ? '12px 10px 8px 14px' : '18px 14px 12px 20px',
      }}>
        <div id={headingId} style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: S.text2 }}>
            {kicker}
          </div>
          <div className="tw-sh-clip" style={{ fontSize: compact ? 15.5 : 17, fontWeight: 600, marginTop: 2 }}>{title}</div>
        </div>
        <button type="button" className="tw-sh-btn" onClick={onClose}
          aria-label="Hide screen list" title="Hide screen list (Esc)"
          {...tw('action', 'shell-rail-collapse')}
          style={{ ...iconButton(36), border: `1px solid ${S.border}` }}>
          <ChevronLeft size={18} aria-hidden />
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 8px 12px 10px' }}>
        {kind === 'editors' ? (
          <>
            {n > 0 && (
              <p style={{ margin: '0 8px 8px', fontSize: 13, lineHeight: 1.45, color: S.text2 }}>
                Records you minimised, with everything typed so far.
              </p>
            )}
            <EditorsList model={model} />
          </>
        ) : model.roots.length === 0 ? (
          <p style={{ margin: '6px 8px', fontSize: 14, color: S.text2, lineHeight: 1.5 }}>{emptyNavMessage}</p>
        ) : folderRoots.length === 0 ? (
          <p style={{ margin: '6px 8px', fontSize: 14, color: S.text2, lineHeight: 1.5 }}>No screens in this folder yet.</p>
        ) : (
          <MenuTree model={model} roots={folderRoots} label={title || 'Screens'} />
        )}
      </div>

      <div style={{
        flexShrink: 0, borderTop: `1px solid ${S.border}`, padding: '10px 8px 12px',
        maxHeight: 'min(280px, 42%)', display: 'flex', flexDirection: 'column', minHeight: 0,
      }}>
        <div role="tablist" aria-label="Quick access" onKeyDown={onQuickKey} style={{
          flexShrink: 0, display: 'flex', gap: 3, padding: 3, margin: '0 4px 6px', borderRadius: 10,
          background: S.surface2,
        }}>
          {quickTabs.map((t) => {
            const on = quick === t.id
            return (
              <button key={t.id} type="button" role="tab" data-tab={t.id} aria-selected={on}
                aria-controls={`${id}-quick`} tabIndex={on ? 0 : -1} onClick={() => setQuick(t.id)}
                {...tw('action', `shell-rail-quick:${t.id}`, { selected: on })}
                style={{
                  flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                  height: 32, border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                  background: on ? S.panel : 'transparent', color: on ? S.text : S.text2,
                  fontSize: 13.5, fontWeight: 600, boxShadow: on ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
                }}>
                {t.label}
                {t.count > 0 && <span style={{ fontSize: 13, fontWeight: 500, color: S.text2 }}>{t.count}</span>}
              </button>
            )
          })}
        </div>
        <div id={`${id}-quick`} role="tabpanel" aria-label={quick === 'pinned' ? 'Pinned' : 'Recent'}
          style={{ overflow: 'auto', minHeight: 0 }}>
          {quick === 'pinned' ? <PinnedList model={model} /> : <RecentList model={model} />}
        </div>
      </div>
    </aside>
  )
})
